// Feature Ideas Page - Browse and vote on feature requests
import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  TextField,
  InputAdornment,
  Chip,
  Stack,
  Button,
  IconButton,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Breadcrumbs,
  Link,
  LinearProgress,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import {
  Search,
  ThumbUp,
  ThumbUpOutlined,
  Add,
  Lightbulb,
  Schedule,
  CheckCircle,
  TrendingUp,
  NewReleases,
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { usePortal } from './PortalLayout';
import { featuresService } from '@/services/collections/features';
import { verifyTurnstileToken } from '@/services/turnstile';
import { TurnstileWidget, type TurnstileWidgetRef } from '@/components/common/TurnstileWidget';
import type { FeatureRequest, FeatureStatus } from '@/types';

const STATUS_CONFIG: Record<FeatureStatus, { label: string; color: string; icon: React.ReactNode }> = {
  under_review: { label: 'Under Review', color: '#94a3b8', icon: <Lightbulb /> },
  planned: { label: 'Planned', color: '#3b82f6', icon: <Schedule /> },
  in_progress: { label: 'In Progress', color: '#f59e0b', icon: <TrendingUp /> },
  released: { label: 'Released', color: '#10b981', icon: <CheckCircle /> },
  declined: { label: 'Declined', color: '#ef4444', icon: <NewReleases /> },
};

const submitSchema = z.object({
  title: z.string().min(5, 'Title must be at least 5 characters'),
  description: z.string().min(20, 'Please provide more detail (at least 20 characters)'),
  email: z.string().email('Valid email required'),
  name: z.string().optional(),
});

type SubmitFormData = z.infer<typeof submitSchema>;

export const FeatureIdeasPage = () => {
  const navigate = useNavigate();
  const { organization, project, branding } = usePortal();

  const [features, setFeatures] = useState<FeatureRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'votes' | 'newest'>('votes');
  const [votedIds, setVotedIds] = useState<string[]>([]);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileWidgetRef>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SubmitFormData>({
    resolver: zodResolver(submitSchema),
  });

  // Load voted features from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(`voted_${organization?.id}`);
    if (stored) {
      setVotedIds(JSON.parse(stored));
    }
  }, [organization?.id]);

  // Fetch features
  useEffect(() => {
    const fetchFeatures = async () => {
      if (!organization || !project) return;

      setLoading(true);
      try {
        const data = await featuresService.getPublic(organization.id, project.id);
        setFeatures(data);
      } catch (error) {
        console.error('Error fetching features:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFeatures();
  }, [organization, project]);

  // Get or create visitor ID
  const getVisitorId = (): string => {
    let visitorId = localStorage.getItem('visitor_id');
    if (!visitorId) {
      visitorId = `visitor_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      localStorage.setItem('visitor_id', visitorId);
    }
    return visitorId;
  };

  // Handle vote
  const handleVote = async (e: React.MouseEvent, feature: FeatureRequest) => {
    e.stopPropagation();
    const visitorId = getVisitorId();
    const hasVoted = votedIds.includes(feature.id);

    try {
      if (hasVoted) {
        await featuresService.unvote(feature.id, visitorId);
        setVotedIds((prev) => prev.filter((id) => id !== feature.id));
        setFeatures((prev) =>
          prev.map((f) =>
            f.id === feature.id ? { ...f, voteCount: f.voteCount - 1 } : f
          )
        );
      } else {
        await featuresService.vote(feature.id, visitorId);
        setVotedIds((prev) => [...prev, feature.id]);
        setFeatures((prev) =>
          prev.map((f) =>
            f.id === feature.id ? { ...f, voteCount: f.voteCount + 1 } : f
          )
        );
      }

      // Save to localStorage
      const newVotedIds = hasVoted
        ? votedIds.filter((id) => id !== feature.id)
        : [...votedIds, feature.id];
      localStorage.setItem(`voted_${organization?.id}`, JSON.stringify(newVotedIds));
    } catch (error) {
      console.error('Error voting:', error);
    }
  };

  // Handle submit new idea
  const onSubmit = async (data: SubmitFormData) => {
    if (!organization || !project) return;
    if (!turnstileToken) return;

    setSubmitting(true);
    try {
      // Verify Turnstile token server-side
      const isValid = await verifyTurnstileToken(turnstileToken);
      if (!isValid) {
        turnstileRef.current?.reset();
        setTurnstileToken(null);
        setSubmitting(false);
        return;
      }

      await featuresService.create({
        organizationId: organization.id,
        projectId: project.id,
        title: data.title,
        description: data.description,
        status: 'under_review',
        submitterEmail: data.email,
        submitterName: data.name,
        voterIds: [],
        voteCount: 0,
        isPublic: true, // Auto-publish for now
        linkedTicketIds: [],
        tags: [],
        createdBy: 'portal',
      }, 'portal');

      reset();
      setSubmitDialogOpen(false);
      setTurnstileToken(null);
      turnstileRef.current?.reset();
      // Refresh features
      const newFeatures = await featuresService.getPublic(organization.id, project.id);
      setFeatures(newFeatures);
    } catch (error) {
      console.error('Error submitting feature:', error);
      turnstileRef.current?.reset();
      setTurnstileToken(null);
    } finally {
      setSubmitting(false);
    }
  };

  // Filter and sort features
  const filteredFeatures = features
    .filter((f) => {
      if (statusFilter !== 'all' && f.status !== statusFilter) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          f.title.toLowerCase().includes(query) ||
          f.description.toLowerCase().includes(query) ||
          f.tags?.some((t) => t.toLowerCase().includes(query))
        );
      }
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'votes') return b.voteCount - a.voteCount;
      return (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0);
    });

  const maxVotes = Math.max(...features.map((f) => f.voteCount), 1);

  // Status counts
  const statusCounts = features.reduce((acc, f) => {
    acc[f.status] = (acc[f.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: branding.primaryColor }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link component={RouterLink} to={`/p/${organization?.slug}`} underline="hover" color="inherit">
          Home
        </Link>
        <Typography color="text.primary">Feature Ideas</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Feature Ideas
          </Typography>
          <Typography color="text.secondary">
            Vote on features you'd like to see or suggest your own ideas
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setSubmitDialogOpen(true)}
          sx={{ bgcolor: branding.primaryColor }}
        >
          Submit Idea
        </Button>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search ideas..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 250 }}
        />

        <ToggleButtonGroup
          value={sortBy}
          exclusive
          onChange={(_, v) => v && setSortBy(v)}
          size="small"
        >
          <ToggleButton value="votes">
            <ThumbUp sx={{ mr: 0.5, fontSize: 18 }} /> Top Voted
          </ToggleButton>
          <ToggleButton value="newest">
            <NewReleases sx={{ mr: 0.5, fontSize: 18 }} /> Newest
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Status Filter Chips */}
      <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Chip
          label={`All (${features.length})`}
          variant={statusFilter === 'all' ? 'filled' : 'outlined'}
          onClick={() => setStatusFilter('all')}
          sx={{
            bgcolor: statusFilter === 'all' ? branding.primaryColor : 'transparent',
            color: statusFilter === 'all' ? 'white' : 'inherit',
          }}
        />
        {Object.entries(STATUS_CONFIG)
          .filter(([status]) => status !== 'declined')
          .map(([status, config]) => (
            <Chip
              key={status}
              icon={config.icon as React.ReactElement}
              label={`${config.label} (${statusCounts[status] || 0})`}
              variant={statusFilter === status ? 'filled' : 'outlined'}
              onClick={() => setStatusFilter(status)}
              sx={{
                bgcolor: statusFilter === status ? config.color : 'transparent',
                color: statusFilter === status ? 'white' : 'inherit',
                '& .MuiChip-icon': {
                  color: statusFilter === status ? 'white' : config.color,
                },
              }}
            />
          ))}
      </Stack>

      {/* Features List */}
      {filteredFeatures.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Lightbulb sx={{ fontSize: 64, color: 'grey.300', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {searchQuery || statusFilter !== 'all' ? 'No ideas match your filters' : 'No ideas yet'}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Be the first to submit a feature idea!
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setSubmitDialogOpen(true)}
            sx={{ bgcolor: branding.primaryColor }}
          >
            Submit Idea
          </Button>
        </Box>
      ) : (
        <Stack spacing={2}>
          {filteredFeatures.map((feature) => {
            const hasVoted = votedIds.includes(feature.id);
            const statusConfig = STATUS_CONFIG[feature.status];

            return (
              <Card
                key={feature.id}
                sx={{
                  transition: 'all 0.2s',
                  '&:hover': {
                    boxShadow: 3,
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <CardActionArea
                  onClick={() => navigate(`/p/${organization?.slug}/ideas/${feature.id}`)}
                >
                  <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    {/* Vote Button */}
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        minWidth: 60,
                      }}
                    >
                      <Tooltip title={hasVoted ? 'Remove vote' : 'Vote for this'}>
                        <IconButton
                          onClick={(e) => handleVote(e, feature)}
                          sx={{
                            border: '2px solid',
                            borderColor: hasVoted ? branding.primaryColor : 'grey.300',
                            bgcolor: hasVoted ? alpha(branding.primaryColor, 0.1) : 'transparent',
                            '&:hover': {
                              borderColor: branding.primaryColor,
                              bgcolor: alpha(branding.primaryColor, 0.1),
                            },
                          }}
                        >
                          {hasVoted ? (
                            <ThumbUp sx={{ color: branding.primaryColor }} />
                          ) : (
                            <ThumbUpOutlined />
                          )}
                        </IconButton>
                      </Tooltip>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        sx={{ mt: 0.5, color: hasVoted ? branding.primaryColor : 'inherit' }}
                      >
                        {feature.voteCount}
                      </Typography>
                    </Box>

                    {/* Content */}
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                        <Typography variant="h6" fontWeight="medium">
                          {feature.title}
                        </Typography>
                        <Chip
                          icon={statusConfig.icon as React.ReactElement}
                          label={statusConfig.label}
                          size="small"
                          sx={{
                            bgcolor: alpha(statusConfig.color, 0.1),
                            color: statusConfig.color,
                            '& .MuiChip-icon': { color: statusConfig.color },
                          }}
                        />
                      </Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          mb: 1,
                        }}
                      >
                        {feature.description}
                      </Typography>

                      {/* Tags */}
                      {feature.tags?.length > 0 && (
                        <Stack direction="row" spacing={0.5} sx={{ mb: 1 }}>
                          {feature.tags.map((tag) => (
                            <Chip key={tag} label={tag} size="small" variant="outlined" />
                          ))}
                        </Stack>
                      )}

                      {/* Progress Bar */}
                      <LinearProgress
                        variant="determinate"
                        value={(feature.voteCount / maxVotes) * 100}
                        sx={{
                          height: 4,
                          borderRadius: 2,
                          bgcolor: alpha(statusConfig.color, 0.2),
                          '& .MuiLinearProgress-bar': {
                            bgcolor: statusConfig.color,
                          },
                        }}
                      />
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            );
          })}
        </Stack>
      )}

      {/* Submit Idea Dialog */}
      <Dialog open={submitDialogOpen} onClose={() => setSubmitDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Submit a Feature Idea</DialogTitle>
        <DialogContent>
          <Box component="form" id="submit-idea-form" onSubmit={handleSubmit(onSubmit)} sx={{ pt: 1 }}>
            <TextField
              {...register('title')}
              label="Title"
              fullWidth
              required
              error={!!errors.title}
              helperText={errors.title?.message}
              placeholder="Brief summary of your idea"
              sx={{ mb: 2 }}
            />
            <TextField
              {...register('description')}
              label="Description"
              fullWidth
              required
              multiline
              rows={4}
              error={!!errors.description}
              helperText={errors.description?.message}
              placeholder="Describe your feature idea in detail. What problem does it solve?"
              sx={{ mb: 2 }}
            />
            <TextField
              {...register('email')}
              label="Your Email"
              type="email"
              fullWidth
              required
              error={!!errors.email}
              helperText={errors.email?.message || "We'll notify you when there's an update"}
              sx={{ mb: 2 }}
            />
            <TextField
              {...register('name')}
              label="Your Name (optional)"
              fullWidth
            />
            <TurnstileWidget
              ref={turnstileRef}
              onSuccess={(token) => setTurnstileToken(token)}
              onExpire={() => setTurnstileToken(null)}
              onError={() => setTurnstileToken(null)}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setSubmitDialogOpen(false); setTurnstileToken(null); turnstileRef.current?.reset(); }} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="submit-idea-form"
            variant="contained"
            disabled={submitting || !turnstileToken}
            sx={{ bgcolor: branding.primaryColor }}
          >
            {submitting ? 'Submitting...' : 'Submit Idea'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
